// MIGRATION: fully on AWS PostgREST.
import { aws } from './awsdb';

// ─── Display helpers ────────────────────────────────────────────────────
//
// Contacts can be a person, a company, or a person AT a company. The data
// model: `contact_person` = the human, `company_name` = the org. Either
// can be null; at least one must be populated.
//
// Display priority is contextual:
//   - "person" mode  (general contacts, AddressBook, cases, invoices —
//                     anywhere the human is who you're talking to)
//   - "company" mode (POs/SupplierPicker — where the legal entity matters)
//
// These helpers centralise the formatting so every screen looks consistent.

/**
 * Primary display string for a contact.
 *   mode = 'person'  → "Nathan Harrington" or "Nathan — Phyto-Therapy"
 *   mode = 'company' → "Phyto-Therapy" or "Phyto-Therapy (c/o Nathan)"
 *   mode = 'auto'    → person if set else company (default)
 */
export function contactDisplayName(c, mode = 'auto') {
  if (!c) return '';
  const person = (c.contact_person || '').trim();
  const company = (c.company_name || '').trim();
  if (mode === 'company') {
    if (company && person) return `${company} (c/o ${person})`;
    return company || person || '';
  }
  if (mode === 'person') {
    if (person && company) return `${person} — ${company}`;
    return person || company || '';
  }
  // auto
  return person || company || '';
}

/**
 * Subtitle / secondary line for a contact, when the screen has room for one.
 * Returns null if there's no secondary info worth showing.
 */
export function contactSubtitle(c, mode = 'auto') {
  if (!c) return null;
  const person = (c.contact_person || '').trim();
  const company = (c.company_name || '').trim();
  if (!person || !company) return null;  // only one field set: nothing to add
  return mode === 'company' ? person : company;
}

/**
 * Does this contact match the user's search query?
 * Matches BOTH person + company so typing "nathan" finds Nathan Harrington
 * even if his company is the listed name, and "phyto" finds Phyto-Therapy.
 */
export function contactMatchesQuery(c, query) {
  if (!c || !query) return false;
  const q = query.toLowerCase();
  return (c.contact_person || '').toLowerCase().includes(q)
      || (c.company_name || '').toLowerCase().includes(q);
}

// ─── Mutators ───────────────────────────────────────────────────────────

// Ensure a contact exists for a supplier name. Creates one if not found.
// Fails silently — never blocks the calling save flow. Supplier ensure is
// always company-keyed (suppliers are companies; person is a property).
export async function ensureContact(supplierName) {
  if (!supplierName || !supplierName.trim()) return;
  const name = supplierName.trim();
  try {
    const { data } = await aws.from('contacts')
      .select('id')
      .ilike('company_name', name)
      .is('archived_at', null)
      .limit(1);
    if (data && data.length > 0) return; // already exists
    await aws.from('contacts').insert([{
      company_name: name, category: 'Supplier', status: 'active', created_by: 'auto'
    }]);
  } catch (e) {
    console.warn('[ensureContact] failed:', e.message);
  }
}
